interface RgbColor {
  r: number
  g: number
  b: number
}

const DEFAULT_PRIMARY = '#0ebfa5'
const DEFAULT_SURFACE = '#ffffff'

function clamp(value: number): number {
  return Math.max(0, Math.min(1, value))
}

function hslToRgb(hue: number, saturation: number, lightness: number): RgbColor {
  const h = ((hue % 360) + 360) % 360
  const s = clamp(saturation / 100)
  const l = clamp(lightness / 100)
  const chroma = (1 - Math.abs(2 * l - 1)) * s
  const x = chroma * (1 - Math.abs(((h / 60) % 2) - 1))
  const match = l - chroma / 2
  const channels =
    h < 60 ? [chroma, x, 0]
      : h < 120 ? [x, chroma, 0]
        : h < 180 ? [0, chroma, x]
          : h < 240 ? [0, x, chroma]
            : h < 300 ? [x, 0, chroma]
              : [chroma, 0, x]
  return {
    r: Math.round((channels[0] + match) * 255),
    g: Math.round((channels[1] + match) * 255),
    b: Math.round((channels[2] + match) * 255),
  }
}

function parseColor(value: string, fallback: string): RgbColor {
  const color = value.trim() || fallback
  const hexMatch = color.match(/^#([0-9a-f]{6})$/i)
  if (hexMatch) {
    const parsed = Number.parseInt(hexMatch[1], 16)
    return { r: (parsed >> 16) & 255, g: (parsed >> 8) & 255, b: parsed & 255 }
  }
  const rgbMatch = color.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/i)
  if (rgbMatch) {
    return { r: Number(rgbMatch[1]), g: Number(rgbMatch[2]), b: Number(rgbMatch[3]) }
  }
  const hslMatch = color.match(/hsla?\(\s*(-?\d+(?:\.\d+)?)\s*[,\s]\s*(\d+(?:\.\d+)?)%\s*[,\s]\s*(\d+(?:\.\d+)?)%/i)
  if (hslMatch) {
    return hslToRgb(Number(hslMatch[1]), Number(hslMatch[2]), Number(hslMatch[3]))
  }
  return parseColor(fallback, DEFAULT_PRIMARY)
}

function resolvedToken(name: string, fallback: string): string {
  if (typeof window === 'undefined') {
    return fallback
  }
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
}

function relativeLuminance(color: RgbColor): number {
  const channels = [color.r, color.g, color.b].map((channel) => {
    const normalized = channel / 255
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4
  })
  return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2]
}

export function chartSegmentHeatColor(intensity: number): string {
  const primaryPercent = Math.round(clamp(intensity) * 100)
  const surfacePercent = 100 - primaryPercent
  return `color-mix(in srgb, var(--slide-chart-segment-primary, var(--slide-chart-1, ${DEFAULT_PRIMARY})) ${primaryPercent}%, var(--slide-bg, ${DEFAULT_SURFACE}) ${surfacePercent}%)`
}

/** The heat mix resolved to one colour in JS (the same mix `chartSegmentHeatColor` asks CSS for). */
function resolvedHeatMix(intensity: number): RgbColor {
  const amount = clamp(intensity)
  const primaryValue =
    resolvedToken('--slide-chart-segment-primary', '') ||
    resolvedToken('--slide-chart-1', DEFAULT_PRIMARY)
  const primary = parseColor(primaryValue, DEFAULT_PRIMARY)
  const surface = parseColor(resolvedToken('--slide-bg', DEFAULT_SURFACE), DEFAULT_SURFACE)
  return {
    r: Math.round(primary.r * amount + surface.r * (1 - amount)),
    g: Math.round(primary.g * amount + surface.g * (1 - amount)),
    b: Math.round(primary.b * amount + surface.b * (1 - amount)),
  }
}

/**
 * `chartSegmentHeatColor` as an `rgb()` string, for a surface that must hold a
 * concrete colour (a chart whose markup is serialised outside a browser). An
 * unresolved token falls back to the default palette, never to black.
 */
export function chartSegmentHeatRgb(intensity: number): string {
  const { r, g, b } = resolvedHeatMix(intensity)
  return `rgb(${r}, ${g}, ${b})`
}

export function chartSegmentHeatTextColor(intensity: number): string {
  return relativeLuminance(resolvedHeatMix(intensity)) > 0.179 ? '#000000' : '#ffffff'
}
